import firebase from "../constants/firebase";
import {
  SCHOOLS,
  STUDENTS,
  SECTIONS,
  CLASSES,
  ATTENDANCE,
  IDX_STUDENTS,
} from "../constants/collections";
import forEach from "lodash/forEach";
import countBy from "lodash/countBy";
import isNil from "lodash/isNil";
import isEmpty from "lodash/isEmpty";
import { asyncForEach } from "./Utils";
import { addStudentToClass } from "./classes";
import { isStudentAdmitted, updateAdmissionStatus } from "./admission";

export const addSchoolToStudent = async (student, school, sy) => {
  const db = firebase.firestore();
  const studentRef = db
    .collection(STUDENTS)
    .doc(student)
    .collection(SCHOOLS)
    .doc(sy);
  return await studentRef.set(
    {
      schools: firebase.firestore.FieldValue.arrayUnion(school),
    },
    { merge: true }
  );
};

export const addSectionToStudent = async (
  student,
  section,
  sy,
  unenroll = false
) => {
  // Admission Gate: Check if student is ADMITTED (or already ENROLLED)
  if (!unenroll) {
    const isAdmitted = await isStudentAdmitted(student, sy);
    if (!isAdmitted) {
      throw new Error(
        "Student is not admitted for this School Year. Please admit them first."
      );
    }
  }

  const db = firebase.firestore();
  const studentRef = db
    .collection(STUDENTS)
    .doc(student)
    .collection(SECTIONS)
    .doc(sy);

  await studentRef.set(
    {
      [SECTIONS]: !unenroll
        ? firebase.firestore.FieldValue.arrayUnion(section)
        : firebase.firestore.FieldValue.arrayRemove(section),
    },
    { merge: true }
  );

  // Update Admission Status to ENROLLED upon successful section assignment
  if (!unenroll) {
    await updateAdmissionStatus(student, sy, "ENROLLED");
  }

  return true;
};

export const addClassToStudent = async (
  student,
  classId,
  sy,
  unenroll = false
) => {
  const db = firebase.firestore();
  const studentRef = db
    .collection(STUDENTS)
    .doc(student)
    .collection(CLASSES)
    .doc(sy);
  return await studentRef.set(
    {
      [CLASSES]: !unenroll
        ? firebase.firestore.FieldValue.arrayUnion(classId)
        : firebase.firestore.FieldValue.arrayRemove(classId),
    },
    { merge: true }
  );
};

export const setSectionClassPopulation = (students) => {
  let total = 0;
  let population = { popTotal: 0, popMale: 0, popFemale: 0 };
  forEach(countBy(students, "gender"), (count, key) => {
    total += count;
    const newKey = `pop${key}`;
    population = {
      ...population,
      [newKey]: count,
      popTotal: total,
    };
  });

  return population;
};

export const addSectionClassesToStudent = async (
  student,
  section,
  sy,
  unenroll = false
) => {
  const db = firebase.firestore();
  const sectionRef = db
    .collection(SECTIONS)
    .doc(section)
    .collection(CLASSES)
    .doc(sy);
  const sectionClassesSnap = await sectionRef.get();

  if (!sectionClassesSnap.exists) return;

  const sectionClasses = sectionClassesSnap.data();

  if (isNil(sectionClasses[CLASSES]) || isEmpty(sectionClasses[CLASSES]))
    return;

  const studentRef = db
    .collection(STUDENTS)
    .doc(student)
    .collection(CLASSES)
    .doc(sy);
  await studentRef.set(
    {
      [CLASSES]: !unenroll
        ? firebase.firestore.FieldValue.arrayUnion(...sectionClasses[CLASSES])
        : firebase.firestore.FieldValue.arrayRemove(...sectionClasses[CLASSES]),
    },
    { merge: true }
  );
  // Add student to each subject class
  return await asyncForEach(sectionClasses[CLASSES], async (classId) => {
    await addStudentToClass(classId, student, sy, unenroll);
  });
};

export const addAttendanceToStudent = async (
  student,
  attendanceId,
  attendanceData
) => {
  const db = firebase.firestore();
  const studentAttendanceRef = db
    .collection(STUDENTS)
    .doc(student)
    .collection(ATTENDANCE)
    .doc(attendanceId);
  return await studentAttendanceRef.set({ ...attendanceData }, { merge: true });
};

export const searchStudent = async ({
  sy,
  schoolId,
  search,
  order = "birthdate",
  limit = 0,
}) => {
  try {
    const db = firebase.firestore();
    const keywords = [search.toLowerCase(), sy, schoolId].join("&");
    let studentRef = db
      .collection(IDX_STUDENTS)
      .where("keywords", "array-contains", keywords)
      .orderBy(order, "desc");
    if (limit) {
      studentRef.limit(limit);
    }
    return await studentRef.get();
  } catch (error) {
    throw error;
  }
};

export const searchEnrollableStudents = async ({
  schoolId,
  sy,
  search,
  gradeLevel,
}) => {
  try {
    // Replicating "Search Registry" behavior: simple Firestore search
    // We search broadly (sy="all") to find the student record,
    // then filter strictly for eligibility in the current SY.
    const studentSnap = await searchStudent({
      sy: "all",
      schoolId,
      search,
      limit: 50,
    });

    const students = [];
    studentSnap.forEach((doc) => {
      students.push({ id: doc.id, ...doc.data() });
    });

    // Filter Logic:
    // 1. Admission Status: ADMITTED or ASSESSED
    // 2. Billing Status: PARTIAL or CLEARED
    // 3. Grade Level: Must match input gradeLevel (section grade)

    // Helper to extract value from sy_X array
    const getValue = (arr) => {
      if (!arr || arr.length === 0) return null;
      // Format: sy_schoolId_value or sy_all_value
      // We look for matches containing current SY and School
      const match = arr.find(
        (s) => s.startsWith(`${sy}_${schoolId}`) || s.startsWith(`${sy}_all`)
      );
      if (match) {
        const parts = match.split("_");
        return parts[parts.length - 1]; // This might be weak if value has underscores
      }
      return null;
    };

    return students.filter((student) => {
      const sStatus = getValue(student.sy_applications);
      const sBilling = getValue(student.sy_billing);
      const sGrade = getValue(student.sy_gradeLevel);

      const validStatus = [
        "ADMITTED",
        "ASSESSED",
        "SUBMITTED",
      ].includes(sStatus);
      const validBilling = ["PARTIAL", "CLEARED"].includes(sBilling);
      const validGrade = sGrade === gradeLevel;

      return validStatus && validBilling && validGrade;
    });
  } catch (error) {
    console.error("Search Enrollable Error:", error);
    throw error;
  }
};
